(function () {
  'use strict';
  if (window.OxxVnextFinder) return;
  /* O-ring size finder. Matching logic, URL prefill (id, cs, units|unit, material, hardness, sku, standard,
     standard_code), standard-code lookup and analytics events are unchanged. WP2 2026-10-03: results, empty state,
     nearby sizes and next steps render as ic-* components (styles: assets/oxx-ic-wp2-finder.css). */
  const norm = value => String(value || '').trim();
  const positive = value => /^(?:\d+(?:\.\d*)?|\.\d+)$/.test(norm(value)) && Number(value) > 0;
  const material = value => ({ VMQ: 'SILICONE', 'BUNA-N': 'NBR', NITRILE: 'NBR' }[norm(value).toUpperCase()] || norm(value).toUpperCase());
  const track = (event, detail) => { window.dataLayer = window.dataLayer || []; window.dataLayer.push(Object.assign({ event }, detail || {})); };
  const codedStandard = value => value === 'AS568' || value === 'ISO3601';
  const standardCode = (standard, value) => {
    const input = norm(value).toUpperCase();
    if (standard === 'AS568') {
      const match = input.match(/^(?:AS\s*568\s*[- ]?)?-?(\d{1,3})$/);
      return match ? match[1].padStart(3, '0') : '';
    }
    return standard === 'ISO3601' && /^[A-E]\d{4}$/.test(input) ? input : '';
  };
  const sizeKey = (id, cs) => Number(id).toFixed(2) + '|' + Number(cs).toFixed(2);
  let standardsReady;
  function loadStandards(url) {
    if (!standardsReady) standardsReady = (async () => {
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw Error('Standard table unavailable');
        const data = await response.json();
        if (data.version !== 1 || !data.AS568?.['214'] || !data.ISO3601?.B0200) throw Error('Standard table unavailable');
        return data;
      } finally { clearTimeout(timeout); }
    })().catch(error => { standardsReady = null; throw error; });
    return standardsReady;
  }
  const matches = (rows, q) => {
    const scale = q.units === 'in' ? 25.4 : 1;
    // Query conversions belong outside the 77k-row loop.
    const id = q.id ? Number(q.id) * scale : null;
    const cs = q.cs ? Number(q.cs) * scale : null;
    const mat = q.material ? material(q.material) : null;
    const hardness = q.hardness ? String(q.hardness).replace(/A$/i, '') : null;
    const sku = q.sku ? q.sku.toLowerCase() : null;
    return rows.filter(row => (id === null || Math.abs(row.id - id) <= 0.005)
      && (cs === null || Math.abs(row.cs - cs) <= 0.005)
      && (mat === null || row.material === mat)
      && (hardness === null || row.hardness === hardness)
      && (sku === null || row.handle.toLowerCase().includes(sku)));
  };
  // Dimension matching is deliberately exact to the catalogue nominal size. Nearby sizes are shown separately and
  // labelled as candidates, never as equivalents; a same-size row with a different material/hardness is review-only.
  const dimensionMatches = (rows, q) => {
    const scale = q.units === 'in' ? 25.4 : 1;
    const id = q.id ? Number(q.id) * scale : null;
    const cs = q.cs ? Number(q.cs) * scale : null;
    return rows.filter(row => (id === null || Math.abs(row.id - id) <= 0.005)
      && (cs === null || Math.abs(row.cs - cs) <= 0.005));
  };
  const groupMatches = (rows, q, table) => {
    const sizes = table ? new Set(Object.values(table).map(size => sizeKey(...size))) : null;
    const allowedSize = row => !sizes || sizes.has(sizeKey(row.id, row.cs));
    const exact = matches(rows, { ...q, sku: '' }).filter(allowedSize);
    const exactHandles = new Set(exact.map(row => row.handle));
    const review = positive(q.id) && positive(q.cs) && (q.material || q.hardness)
      ? dimensionMatches(rows, q).filter(row => !exactHandles.has(row.handle) && allowedSize(row)) : [];
    return { exact: exact.sort(compare), review: review.sort(compare) };
  };
  // Grade order inside one size: the common families first, then hardness.
  const MAT_ORDER = { NBR: 0, FKM: 1, EPDM: 2, SILICONE: 3, FFKM: 4 };
  const matRank = m => (m in MAT_ORDER ? MAT_ORDER[m] : 9);
  const compare = (a, b) => a.id - b.id || a.cs - b.cs || matRank(a.material) - matRank(b.material) || Number(a.hardness) - Number(b.hardness);
  const fromIndex = index => Object.entries(index.exact || {}).map(([key, handle]) => {
    const [id, cs, mat, hardness] = key.split('|');
    return { id: Number(id), cs: Number(cs), material: mat, hardness, handle };
  }).filter(row => row.id > 0 && row.cs > 0 && /^[a-z0-9-]+$/.test(row.handle));
  // v2 compact size index (assets/oxx-ic-size-index.json): "id,cs,N70F75...;..." plus handle exceptions.
  const COMPACT_MAT = { N: ['NBR', 'nbr'], F: ['FKM', 'fkm'], K: ['FFKM', 'ffkm'], E: ['EPDM', 'epdm'], S: ['SILICONE', 'silicone'] };
  const fromCompact = data => {
    const rows = [], x = data.x || {};
    const dash = v => v.replace('.', '-');
    data.sizes.split(';').forEach(s => {
      const p = s.split(','); if (p.length < 3) return;
      p[2].replace(/([A-Z])(\d{2})/g, (_, m, h) => {
        const k = COMPACT_MAT[m]; if (!k) return '';
        const handle = x[p[0] + 'x' + p[1] + m + h] || ('o-ring-' + dash(p[0]) + 'x' + dash(p[1]) + '-' + k[1] + '-' + h + 'a');
        rows.push({ id: Number(p[0]), cs: Number(p[1]), material: k[0], hardness: h, handle });
        return '';
      });
    });
    return rows;
  };
  let ready;
  async function load(url) {
    if (!ready) ready = (async () => {
      const existing = window.OXX_FINDER_INDEX;
      if (existing && existing.exact) return fromIndex(existing);
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Catalogue unavailable');
        const data = await response.json();
        if (data.sizes) return fromCompact(data);
        if (!data.exact) throw new Error('Catalogue unavailable');
        return fromIndex(data);
      } finally { clearTimeout(timeout); }
    })().catch(error => { ready = null; throw error; });
    return ready;
  }
  // Listed sizes close to the query, grouped per size. Candidates only: a different ID or CS is not an equivalent.
  function nearbySizes(rows, id, cs, limit) {
    const idTol = Math.max(0.3, id * 0.04), csTol = 0.16, by = new Map();
    for (const r of rows) {
      const dId = r.id - id, dCs = r.cs - cs;
      if (Math.abs(dId) > idTol || Math.abs(dCs) > csTol) continue;
      if (Math.abs(dId) < 0.006 && Math.abs(dCs) < 0.006) continue;
      const key = sizeKey(r.id, r.cs);
      const entry = by.get(key) || { id: r.id, cs: r.cs, dId, dCs, n: 0, score: Math.abs(dId) / idTol + Math.abs(dCs) / csTol };
      entry.n++; by.set(key, entry);
    }
    return [...by.values()].sort((a, b) => a.score - b.score).slice(0, limit || 6);
  }
  function el(tag, text, className) {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (className) node.className = className;
    return node;
  }
  const mm2 = n => Number(n).toFixed(2);
  const handoffKeys = ['media', 'temperature', 'temperature_unit', 'pressure', 'pressure_unit', 'motion', 'application', 'context'];
  function quoteUrl(q, mode) {
    const params = new URLSearchParams({ source: 'vnext_finder' });
    ['id', 'cs', 'material', 'hardness', 'sku', 'standard', 'standard_code'].forEach(key => { if (q[key]) params.set(key, q[key]); });
    if (q.standard === 'ISO3601') params.set('standard_class', 'B');
    // Existing RFQ contract accepts mm dimensions. Keep original units separately.
    if (q.units === 'in') {
      ['id', 'cs'].forEach(key => { if (positive(q[key])) params.set(key, String(Number((Number(q[key]) * 25.4).toFixed(5)))); });
      params.set('original_unit', 'in');
      ['id', 'cs'].forEach(key => { if (q[key]) params.set('original_' + key, q[key]); });
    } else if (q.units === 'mm') {
      // Normalize valid shorthand such as .5 or 20. for the RFQ parser.
      ['id', 'cs'].forEach(key => { if (positive(q[key])) params.set(key, String(Number(Number(q[key]).toFixed(5)))); });
    }
    if (q.units === 'mm' || q.units === 'in') params.set('unit', 'mm');
    if (positive(q.id) && positive(q.cs)) {
      const s = q.units === 'in' ? 25.4 : 1;
      params.set('size_or_sku', mm2(Number(q.id) * s) + ' x ' + mm2(Number(q.cs) * s) + ' mm');
    } else if (q.standard && q.standard_code) params.set('size_or_sku', q.standard + ' ' + q.standard_code);
    handoffKeys.forEach(key => { if (q[key]) params.set(key, q[key]); });
    if (mode) params.set('request_mode', mode);
    return '/pages/bulk-quote?' + params;
  }
  function engineerUrl(q) {
    const params = new URLSearchParams({ view: 'engineer-pro', source: 'vnext_finder' });
    ['id', 'cs', 'material', 'hardness', 'sku', 'standard', 'standard_code'].forEach(key => { if (q[key]) params.set(key, q[key]); });
    if (q.standard === 'ISO3601') params.set('standard_class', 'B');
    if (q.units === 'mm' || q.units === 'in') params.set('unit', q.units);
    handoffKeys.forEach(key => { if (q[key]) params.set(key, q[key]); });
    return '/pages/bulk-quote?' + params;
  }
  function init(root) {
    if (root.dataset.finderReady) return;
    root.dataset.finderReady = 'true';
    const form = root.querySelector('[data-finder-form]');
    const output = root.querySelector('[data-finder-results]');
    const error = root.querySelector('[data-finder-error]');
    if (!form || !output || !error) return;
    const initial = output.innerHTML;
    const icons = root.querySelector('template[data-fd-icons]');
    const icon = name => { const s = icons && icons.content.querySelector('[data-i="' + name + '"]'); return s && s.firstElementChild ? s.firstElementChild.cloneNode(true) : document.createTextNode(''); };
    // No query at all: restore the quick-start empty state. Otherwise show a short prompt.
    const renderPrompt = text => {
      output.hidden = false;
      output.dataset.state = 'empty';
      output.setAttribute('aria-busy', 'false');
      if (!text || output.closest('.oxx-home-live')) output.innerHTML = initial;
      else output.replaceChildren(el('p', text, 'ic-fd-state'));
    };
    const standard = form.elements.standard, code = form.elements.standard_code;
    const lookup = form.querySelector('[data-standard-lookup]'), status = form.querySelector('[data-standard-status]');
    const options = form.querySelector('[data-standard-options]');
    // Legacy mobile filter dialog (still supported when present; the ic finder keeps the form visible instead).
    const filterDialog = root.querySelector('[data-finder-filter-dialog]');
    const filterToggle = root.querySelector('[data-finder-filter-toggle]');
    const filterClose = root.querySelector('[data-finder-filter-close]');
    const filterBody = root.querySelector('[data-finder-filter-body]');
    const filterHost = root.querySelector('.oxx-finder-workspace-form');
    const filterCard = root.querySelector('.oxx-finder-card');
    const mobileMedia = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
    const restoreFilters = () => { if (filterHost && filterCard && filterCard.parentElement !== filterHost) filterHost.append(filterCard); };
    const openFilters = () => {
      if (!filterDialog || !filterBody || !filterCard || !mobileMedia?.matches) return false;
      filterBody.append(filterCard);
      if (!filterDialog.open) filterDialog.showModal();
      if (filterToggle) filterToggle.setAttribute('aria-expanded', 'true');
      return true;
    };
    const closeFilters = () => { if (filterDialog?.open) filterDialog.close(); };
    if (filterDialog && filterToggle) {
      filterToggle.addEventListener('click', openFilters);
      if (filterClose) filterClose.addEventListener('click', closeFilters);
      filterDialog.addEventListener('close', () => { restoreFilters(); filterToggle.setAttribute('aria-expanded', 'false'); filterToggle.focus(); });
      const onWidth = () => { if (!mobileMedia.matches) { closeFilters(); restoreFilters(); } };
      if (mobileMedia?.addEventListener) mobileMedia.addEventListener('change', onWidth);
    }
    let resolved = null, lastUnit = form.elements.units?.value || '', checkLinkConflict = false;
    // Service conditions are inherited from the upstream tool; size edits keep them attached.
    const inherited = {}, incoming = new URLSearchParams(location.search);
    handoffKeys.forEach(key => { const value = incoming.get(key); if (value) inherited[key] = norm(value).slice(0, key === 'context' ? 1500 : 300); });
    const readQuery = () => Object.assign({}, inherited, Object.fromEntries([...new FormData(form)].map(([key, value]) => [key, norm(value)])));
    const setStatus = text => { if (status) { status.textContent = text; status.hidden = !text; } };
    const setUnit = value => {
      const radio = form.querySelector('input[name="units"][value="' + value + '"]');
      if (radio) radio.checked = true;
      lastUnit = value;
    };
    const showStandard = () => {
      if (!code || !lookup) return;
      const supported = codedStandard(standard.value);
      lookup.hidden = !supported; code.disabled = !supported;
      form.querySelector('[data-standard-code-label]').textContent = standard.value === 'AS568' ? 'Dash number' : 'Size code';
      code.placeholder = standard.value === 'AS568' ? 'e.g. -214' : 'e.g. B0200';
      code.removeAttribute('aria-invalid');
      setStatus(standard.value === 'DIN3771' ? 'By dimensions · confirm the drawing requirement' : '');
    };
    const fillDimensions = size => {
      if (!lastUnit) setUnit('mm');
      ['id', 'cs'].forEach((key, i) => { form.elements[key].value = lastUnit === 'in' ? String(Number((size[i] / 25.4).toFixed(8))) : size[i].toFixed(2); });
    };
    const clearResolved = () => {
      if (resolved) ['id', 'cs'].forEach(key => { form.elements[key].value = ''; });
      resolved = null;
    };
    const pageSize = 20;
    let sequence = 0, timer, skuController, commerceView, page = 0, result = [], reviewResult = [], query = {}, activeGroup = 'exact', allRows = [];
    const invalidate = () => {
      sequence++;
      if (commerceView) { commerceView.cancel(); commerceView = null; }
      if (skuController) skuController.abort();
      skuController = null;
    };
    const inch = query => query.units === 'in';
    const sizeLabel = (id, cs) => inch(query) ? (id / 25.4).toFixed(3) + ' × ' + (cs / 25.4).toFixed(3) + ' in' : mm2(id) + ' × ' + mm2(cs) + ' mm';
    const querySize = () => {
      if (!positive(query.id) || !positive(query.cs)) return null;
      const s = inch(query) ? 25.4 : 1;
      return { id: Number(query.id) * s, cs: Number(query.cs) * s };
    };
    const finderHref = (id, cs, source) => {
      const p = new URLSearchParams({ view: 'find-o-rings', units: 'mm', id: mm2(id), cs: mm2(cs) });
      if (query.material) p.set('material', query.material);
      if (query.hardness) p.set('hardness', query.hardness);
      p.set('source', source || 'finder-nearby');
      return '/pages/size-guide?' + p;
    };
    // Filters and references for the current result, shown once under the result title.
    const renderIdentity = size => {
      const wrap = el('div', undefined, 'ic-fd-meta');
      wrap.setAttribute('aria-label', 'Your current search');
      const addChip = (text, key) => {
        const chip = el('span', undefined, 'ic-chip ic-fd-meta__chip'); chip.append(el('span', text));
        if (key) {
          const remove = el('button', '×', 'ic-fd-meta__x'); remove.type = 'button';
          remove.setAttribute('aria-label', 'Remove ' + text + ' filter');
          remove.addEventListener('click', () => {
            form.elements[key].value = '';
            if (key === 'standard') { if (code) code.value = ''; resolved = null; checkLinkConflict = false; if (options) options.replaceChildren(); showStandard(); }
            clearTimeout(timer); update(false);
          });
          chip.append(remove);
        }
        wrap.append(chip);
      };
      if (size) {
        // Published size reference (AS568 / ISO 3601 nominal), the same for every grade at this size.
        const ref = el('span', undefined, 'ic-chip ic-fd-meta__chip ic-fd-meta__ref'); ref.hidden = true; wrap.append(ref);
        loadStandards(root.dataset.standardsUrl).then(data => {
          const hits = [];
          [['AS568', c => 'AS568-' + c], ['ISO3601', c => 'ISO 3601 ' + c]].forEach(([sys, label]) => {
            if (query.standard === sys && query.standard_code) return; // already shown as the standard chip
            const hit = Object.entries(data[sys] || {}).find(([, d]) => Math.abs(d[0] - size.id) <= 0.005 && Math.abs(d[1] - size.cs) <= 0.005);
            if (hit) hits.push(label(hit[0]));
          });
          if (!hits.length || !ref.isConnected) return;
          ref.replaceChildren(el('span', 'Size reference', 'ic-fd-meta__k'), el('b', hits.join(' · '), 'ic-num'));
          ref.hidden = false;
        }).catch(() => {});
      }
      if (query.standard) {
        const c = query.standard_code;
        addChip(({ AS568: c ? 'AS568-' + c : 'AS568 sizes only', ISO3601: c ? 'ISO 3601 ' + c : 'ISO 3601 sizes only', DIN3771: 'DIN 3771', METRIC: 'Metric sizes only' })[query.standard] || query.standard, 'standard');
      }
      if (query.material) addChip(material(query.material) === 'SILICONE' ? 'Silicone' : material(query.material), 'material');
      if (query.hardness) addChip(query.hardness.replace(/A$/i, '') + ' Shore A', 'hardness');
      if (query.sku) addChip('Part ' + query.sku);
      return wrap;
    };
    const editSearch = () => { openFilters(); const field = form.elements.id || form.elements.sku; if (field) { field.focus(); field.scrollIntoView({ block: 'center' }); } };
    const card = (href, iconName, title, text) => {
      const a = el('a', undefined, 'ic-card ic-fd-help'); a.href = href;
      const ic = el('span', undefined, 'ic-fd-help__ic'); ic.append(icon(iconName));
      const t = el('span'); t.append(el('strong', title), el('small', text));
      a.append(ic, t, icon('chev'));
      return a;
    };
    const renderNearby = size => {
      const mat = query.material ? material(query.material) : null, hard = query.hardness ? String(query.hardness).replace(/A$/i, '') : null;
      const pool = mat || hard ? allRows.filter(r => (!mat || r.material === mat) && (!hard || r.hardness === hard)) : allRows;
      const near = nearbySizes(pool, size.id, size.cs, 6);
      if (!near.length) return null;
      const box = el('section', undefined, 'ic-fd-near'); box.setAttribute('aria-labelledby', 'IcFdNearH');
      const top = el('div', undefined, 'ic-fd-near__head');
      const h = el('h3', 'Nearby listed sizes', 'ic-h3'); h.id = 'IcFdNearH';
      top.append(h, el('span', 'Candidates, not equivalents', 'ic-tag ic-fd-near__tag'));
      box.append(top, el('p', 'A different ID or CS changes stretch and squeeze in the groove. Check the gland, or ask us, before you substitute.', 'ic-small ic-fd-near__note'));
      // Catalogue sizes are millimetres; nearby chips stay in mm so small differences remain visible in inch mode too.
      const sign = v => { const r = Math.round(v * 100) / 100; return (r > 0 ? '+' : r < 0 ? '−' : '') + Math.abs(r).toFixed(2); };
      const ul = el('ul', undefined, 'ic-fd-chips');
      near.forEach(nb => {
        const li = el('li'), a = el('a', undefined, 'ic-chip ic-fd-chip ic-fd-chip--near'); a.href = finderHref(nb.id, nb.cs);
        a.append(el('span', mm2(nb.id) + ' × ' + mm2(nb.cs) + ' mm', 'ic-num'), el('small', 'ID ' + sign(nb.dId) + ' · CS ' + sign(nb.dCs) + ' · ' + nb.n + (nb.n === 1 ? ' grade' : ' grades'), 'ic-num'));
        li.append(a); ul.append(li);
      });
      box.append(ul);
      return box;
    };
    const renderNext = (size, withQuote) => {
      const box = el('div', undefined, 'ic-fd-next');
      if (size) box.append(card('/pages/size-guide?' + new URLSearchParams({ view: 'oxx-groove-design', mode: 'check', units: 'mm', id: mm2(size.id), cs: mm2(size.cs), source: 'finder' }), 'wrench', 'Check the groove', 'Squeeze, stretch and gland fill for this size.'));
      box.append(card('/pages/size-guide?view=measure&source=finder', 'ruler', 'Not sure of the size? Measure it', 'Two numbers from an old ring or a drawing.'));
      if (withQuote) box.append(card(quoteUrl(query, 'NEED_HELP'), 'file', 'Volume, other grades or custom', 'Send the size for a quote. Fast response.'));
      return box;
    };
    const display = () => {
      if (commerceView) { commerceView.cancel(); commerceView = null; }
      output.hidden = false;
      output.replaceChildren();
      const reviewOnly = activeGroup === 'review';
      const source = reviewOnly ? reviewResult : result;
      const size = querySize();
      const n = source.length;
      const single = n > 0 && source.every(r => Math.abs(r.id - source[0].id) < 0.006 && Math.abs(r.cs - source[0].cs) < 0.006);
      const head = el('div', undefined, 'ic-fd-res__head');
      const titles = el('div', undefined, 'ic-fd-res__titles');
      titles.append(el('p', reviewOnly ? 'Same size · other grades' : result.length ? (size ? 'Exact size match' : 'Matching your filters') : 'No exact match', 'ic-eyebrow' + (result.length && !reviewOnly ? ' ic-eyebrow--accent' : '')));
      const grades = n.toLocaleString('en') + (n === 1 ? ' grade' : ' grades');
      // A coded standard filter (AS568 / ISO 3601) can hide a size that is listed outside that standard.
      const stdName = ({ AS568: 'AS568', ISO3601: 'ISO 3601' })[query.standard] || '';
      const listedOther = !n && size && stdName && allRows.length ? dimensionMatches(allRows, query).length : 0;
      let title;
      if (single) title = sizeLabel(source[0].id, source[0].cs) + ': ' + grades;
      else if (n) title = n.toLocaleString('en') + (n === 1 ? ' matching grade' : ' matching grades');
      else if (size) title = (listedOther ? 'No ' + stdName + ' size at ' : 'No listed O-ring at ') + sizeLabel(size.id, size.cs);
      else title = 'No listed O-ring matches these filters';
      titles.append(el('h2', title, 'ic-h2 ic-fd-res__title'));
      if (single && inch(query)) titles.append(el('p', 'Catalogue size ' + mm2(source[0].id) + ' × ' + mm2(source[0].cs) + ' mm', 'ic-small ic-fd-res__alt'));
      titles.append(renderIdentity(single ? source[0] : size));
      head.append(titles);
      if (reviewResult.length) {
        const tabs = el('div', undefined, 'ic-fd-tabs'); tabs.setAttribute('role', 'group'); tabs.setAttribute('aria-label', 'Result groups');
        [['exact', size ? 'Exact match' : 'Matches', result.length], ['review', 'Same size, other grades', reviewResult.length]].forEach(([group, label, count]) => {
          const button = el('button', undefined, 'ic-fd-tab'); button.type = 'button'; button.dataset.group = group;
          button.append(label + ' ', el('span', String(count), 'ic-num ic-fd-tab__n'));
          button.setAttribute('aria-pressed', String(activeGroup === group));
          button.addEventListener('click', () => { if (activeGroup === group) return; activeGroup = group; page = 0; display(); output.querySelector('[data-group="' + group + '"]')?.focus(); });
          tabs.append(button);
        });
        head.append(tabs);
      }
      output.append(head);
      if (n) {
        const visible = source.slice(page * pageSize, (page + 1) * pageSize);
        if (window.OxxFinderCommerce) {
          commerceView = window.OxxFinderCommerce.render({ output, rows: visible, query, reviewOnly, showSize: !single });
        } else {
          // Safe links remain usable if the optional commerce enhancement cannot load.
          const list = el('ul', undefined, 'ic-fd-fallback');
          visible.forEach(row => {
            const item = el('li');
            const a = el('a', mm2(row.id) + ' × ' + mm2(row.cs) + ' mm · ' + (row.material === 'SILICONE' ? 'Silicone' : row.material) + ' ' + row.hardness + ' Shore A', 'ic-link');
            a.href = '/products/' + row.handle; item.append(a); list.append(item);
          });
          output.append(list);
        }
        if (n > pageSize) {
          const controls = el('div', undefined, 'ic-fd-pages');
          const prev = el('button', 'Previous', 'ic-btn ic-btn--ghost ic-btn--sm'); prev.type = 'button'; prev.disabled = page === 0;
          const next = el('button', 'Next', 'ic-btn ic-btn--ghost ic-btn--sm'); next.type = 'button'; next.disabled = (page + 1) * pageSize >= n;
          prev.addEventListener('click', () => { page--; display(); output.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
          next.addEventListener('click', () => { page++; display(); output.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
          controls.append(prev, el('span', (page * pageSize + 1) + '–' + Math.min((page + 1) * pageSize, n) + ' of ' + n.toLocaleString('en'), 'ic-num'), next);
          output.append(controls);
        }
      } else {
        const none = el('div', undefined, 'ic-card ic-fd-none');
        const actions = el('div', undefined, 'ic-fd-none__acts');
        if (listedOther) {
          none.append(el('p', 'This size is listed, but it is not a published ' + stdName + ' size. Remove the ' + stdName + ' filter to see every grade we list at this size.'));
          const all = el('button', 'Show all ' + listedOther + (listedOther === 1 ? ' grade' : ' grades'), 'ic-btn ic-btn--dark'); all.type = 'button';
          all.addEventListener('click', () => { standard.value = ''; if (code) code.value = ''; resolved = null; checkLinkConflict = false; if (options) options.replaceChildren(); showStandard(); clearTimeout(timer); update(false); });
          actions.append(all);
        } else {
          if (reviewResult.length) none.append(el('p', 'No grade matches every filter. The same size is listed in other materials or hardness: open "Same size, other grades" above, or remove a filter.'));
          else none.append(el('p', 'We can make it as a custom size. With an existing mould, custom production usually takes 3–7 days. New mould tooling and production take 13 days standard or as fast as 7 days rush, subject to quote confirmation. Shipping time is additional.'));
          const req = el('a', 'Request this size', 'ic-btn ic-btn--dark'); req.href = quoteUrl(query, 'CUSTOM');
          actions.append(req);
        }
        const edit = el('button', 'Edit search', 'ic-btn ic-btn--ghost'); edit.type = 'button'; edit.addEventListener('click', editSearch);
        actions.append(edit); none.append(actions); output.append(none);
      }
      if (size && allRows.length) { const near = renderNearby(size); if (near) output.append(near); }
      output.append(renderNext(size, n > 0));
    };
    async function update(submitted) {
      invalidate();
      const run = sequence;
      query = readQuery();
      activeGroup = 'exact';
      error.hidden = true; page = 0;
      if (code) code.removeAttribute('aria-invalid');
      if (!['id', 'cs', 'material', 'hardness', 'sku', 'standard'].some(key => query[key])) {
        renderPrompt(); return;
      }
      let issue = '';
      let standardTable = null;
      if (codedStandard(query.standard)) {
        output.hidden = false; output.dataset.state = 'loading'; output.setAttribute('aria-busy', 'true'); output.replaceChildren(el('p', 'Looking up the size table…', 'ic-fd-state'));
        try {
          standardTable = (await loadStandards(root.dataset.standardsUrl))[query.standard];
        } catch (_) {
          if (run !== sequence) return;
          if (submitted) closeFilters();
          output.dataset.state = 'error'; output.setAttribute('aria-busy', 'false'); output.replaceChildren(el('p', 'The size table could not load.', 'ic-fd-state ic-fd-state--error'));
          const retry = el('button', 'Try again', 'ic-btn ic-btn--dark ic-btn--sm'); retry.type = 'button'; retry.addEventListener('click', () => update(false)); output.append(retry); return;
        }
        if (run !== sequence) return;
        const prefix = norm(query.standard_code).toUpperCase().replace(/^AS\s*568\s*/, '').replace(/^-/, '');
        if (options) {
          options.replaceChildren();
          Object.keys(standardTable).sort().filter(key => !prefix || key.startsWith(prefix)).slice(0, 12).forEach(key => {
            const option = el('option'); option.value = query.standard === 'AS568' ? '-' + key : key;
            option.label = standardTable[key].map(n => n.toFixed(2)).join(' × ') + ' mm'; options.append(option);
          });
        }
        if (query.standard_code) {
          const canonical = standardCode(query.standard, query.standard_code);
          const size = canonical ? standardTable[canonical] : null;
          if (!size) { issue = 'Number not found. Check the code or search by dimensions.'; if (code) code.setAttribute('aria-invalid', 'true'); setStatus(''); }
          else {
            const scale = query.units === 'in' ? 25.4 : 1;
            const conflict = checkLinkConflict && ['id', 'cs'].some((key, i) => query[key] && (!positive(query[key]) || !query.units || Math.abs(Number(query[key]) * scale - size[i]) > 0.005));
            if (conflict) {
              closeFilters();
              error.textContent = 'The number and the entered dimensions differ. Choose which size to use.'; error.hidden = false;
              const box = el('div', undefined, 'ic-card ic-fd-none');
              box.append(el('p', 'The number and the entered dimensions differ. Choose which size to use.'));
              const acts = el('div', undefined, 'ic-fd-none__acts');
              const useCode = el('button', 'Use ' + (query.standard === 'AS568' ? 'AS568-' : 'ISO ') + canonical + ' dimensions', 'ic-btn ic-btn--dark'); useCode.type = 'button';
              useCode.addEventListener('click', () => { checkLinkConflict = false; update(false); });
              const useSize = el('button', 'Use entered dimensions', 'ic-btn ic-btn--ghost'); useSize.type = 'button';
              useSize.addEventListener('click', () => { checkLinkConflict = false; code.value = ''; standard.value = ''; resolved = null; showStandard(); update(false); });
              acts.append(useCode, useSize); box.append(acts); output.replaceChildren(box); return;
            }
            resolved = { standard: query.standard, code: canonical, size };
            fillDimensions(size); query = readQuery(); query.standard_code = canonical;
            setStatus((query.standard === 'AS568' ? 'AS568-' : 'ISO 3601 Class B · ') + canonical + ' = ' + size.map(n => n.toFixed(2)).join(' × ') + ' mm');
          }
        } else if (!query.id && !query.cs) {
          const prompt = query.standard === 'AS568' ? 'Enter a dash number (e.g. -214) or the dimensions.' : 'Enter a Class B size code or the dimensions.';
          setStatus(''); renderPrompt(prompt); return;
        } else setStatus('Matching published nominal sizes');
      } else if (query.standard && !query.id && !query.cs) {
        setStatus('');
        renderPrompt('Enter the inside diameter and cross-section to find your size.'); return;
      } else if (query.standard_code) issue = 'Choose a standard for this number.';
      if ((query.id && !positive(query.id)) || (query.cs && !positive(query.cs))) issue = 'Enter dimensions greater than zero.';
      else if ((query.id || query.cs) && !['mm', 'in'].includes(query.units)) issue = 'Choose mm or in to match dimensions.';
      if (issue) {
        error.textContent = issue; error.hidden = false; output.replaceChildren(); output.hidden = false; output.dataset.state = 'invalid'; output.setAttribute('aria-busy', 'false');
        const a = el('a', 'Ask us about this requirement', 'ic-link'); a.href = quoteUrl(query, 'NEED_HELP');
        const p = el('p', undefined, 'ic-fd-state'); p.append(issue + ' ', a); output.append(p); return;
      }
      if (submitted) closeFilters();
      output.hidden = false; output.dataset.state = 'loading'; output.setAttribute('aria-busy', 'true'); output.replaceChildren(el('p', 'Finding matching sizes…', 'ic-fd-state'));
      try {
        const rows = await load(root.dataset.indexUrl || window.OXX_FINDER_INDEX_URL);
        if (run !== sequence) return;
        allRows = rows;
        let candidates = rows;
        if (query.sku) {
          const url = new URL('/search/suggest.json', location.origin);
          url.searchParams.set('q', query.sku); url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', '10'); url.searchParams.set('resources[options][fields]', 'title,variants.sku');
          skuController = new AbortController();
          const response = await fetch(url, { credentials: 'same-origin', signal: skuController.signal });
          if (!response.ok) throw Error('Search unavailable');
          const data = await response.json(); if (run !== sequence) return;
          const handles = new Set((data.resources?.results?.products || []).map(p => p.handle));
          candidates = rows.filter(row => handles.has(row.handle));
        }
        const groups = groupMatches(candidates, query, standardTable);
        result = groups.exact; reviewResult = groups.review; output.dataset.state = 'ready'; output.setAttribute('aria-busy', 'false'); display();
        if (submitted) {
          track('oxx_finder_search_result', { outcome: result.length ? 'match' : reviewResult.length ? 'review_only' : 'no_match', exact_count: result.length, review_count: reviewResult.length, standard: query.standard || '', material: material(query.material) || '', hardness: String(query.hardness || '').replace(/A$/i, ''), has_size: Boolean(positive(query.id) && positive(query.cs)) });
          if (output.getBoundingClientRect().top > window.innerHeight * 0.6) output.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (_) {
        if (run !== sequence) return;
        output.dataset.state = 'error'; output.setAttribute('aria-busy', 'false');
        const box = el('div', undefined, 'ic-card ic-fd-none');
        box.append(el('p', 'The catalogue could not load. Try again, or send your size and we will check it.'));
        const acts = el('div', undefined, 'ic-fd-none__acts');
        const retry = el('button', 'Try again', 'ic-btn ic-btn--dark'); retry.type = 'button'; retry.addEventListener('click', () => update(false));
        const quote = el('a', 'Send the size', 'ic-btn ic-btn--ghost'); quote.href = quoteUrl(query, 'NEED_HELP');
        acts.append(retry, quote); box.append(acts); output.replaceChildren(box);
      }
    }
    const prepareInput = event => {
      const name = event.target.name;
      if (['id', 'cs', 'standard', 'standard_code', 'units'].includes(name)) checkLinkConflict = false;
      if (name === 'id' || name === 'cs') { resolved = null; if (code) code.value = ''; setStatus(''); }
      if (name === 'standard_code') { clearResolved(); setStatus(''); }
      if (name === 'standard') {
        resolved = null; if (code) code.value = ''; if (options) options.replaceChildren(); showStandard();
        if (standard.value === 'METRIC') {
          if (lastUnit === 'in') ['id', 'cs'].forEach(key => { if (positive(form.elements[key].value)) form.elements[key].value = String(Number((Number(form.elements[key].value) * 25.4).toFixed(8))); });
          setUnit('mm');
        }
      }
      if (name === 'units') {
        const unit = event.target.value;
        if (lastUnit && lastUnit !== unit) {
          if (resolved) { setUnit(unit); fillDimensions(resolved.size); }
          else ['id', 'cs'].forEach(key => { if (positive(form.elements[key].value)) form.elements[key].value = String(Number((Number(form.elements[key].value) * (unit === 'in' ? 1 / 25.4 : 25.4)).toFixed(8))); });
        }
        lastUnit = unit;
        if (standard.value === 'METRIC' && unit === 'in') { standard.value = ''; showStandard(); }
      }
    };
    form.addEventListener('input', event => {
      // Selects and radios also emit change; prepare them only once.
      if (event.target.matches('select,input[type=radio]')) { invalidate(); return; }
      prepareInput(event); invalidate(); clearTimeout(timer); timer = setTimeout(() => update(false), 250);
    });
    form.addEventListener('change', event => {
      if (!event.target.matches('select,input[type=radio]')) return;
      if (event.target.name === 'condition') return; // handled by the confidence gate only
      prepareInput(event); clearTimeout(timer); update(false);
    });
    form.addEventListener('submit', event => { event.preventDefault(); clearTimeout(timer); update(true); });
    form.addEventListener('reset', () => { clearTimeout(timer); invalidate(); resolved = null; lastUnit = ''; checkLinkConflict = false; standard.value = ''; showStandard(); if (options) options.replaceChildren(); error.hidden = true; output.innerHTML = initial; output.hidden = false; output.dataset.state = 'empty'; setTimeout(() => setUnit('mm'), 0); });
    const params = new URLSearchParams(location.search);
    ['id', 'cs', 'material', 'hardness', 'sku', 'standard', 'standard_code'].forEach(key => { if (params.has(key) && form.elements[key]) form.elements[key].value = params.get(key); });
    const units = params.get('units') || params.get('unit');
    if (units === 'mm' || units === 'in') setUnit(units);
    if (standard.value === 'METRIC') {
      if (lastUnit === 'in') ['id', 'cs'].forEach(key => { if (positive(form.elements[key].value)) form.elements[key].value = String(Number((Number(form.elements[key].value) * 25.4).toFixed(8))); });
      setUnit('mm');
    }
    showStandard();
    checkLinkConflict = params.has('standard_code') && (params.has('id') || params.has('cs'));
    if (['id', 'cs', 'material', 'hardness', 'sku', 'standard', 'standard_code'].some(key => params.has(key))) update(false);
  }
  function initHelp() {
    document.querySelectorAll('[data-help]').forEach(button => {
      if (button.dataset.helpReady) return; button.dataset.helpReady = 'true';
      button.setAttribute('aria-haspopup', 'dialog');
      button.addEventListener('click', () => {
        const dialog = el('dialog', undefined, 'oxx-vnext-help-dialog');
        const title = el('h2', button.getAttribute('aria-label') || 'More information');
        title.id = 'oxx-help-title'; dialog.setAttribute('aria-labelledby', title.id);
        const close = el('button', 'Close'); close.type = 'button';
        dialog.append(title, el('p', button.dataset.help), close); document.body.append(dialog);
        const cleanup = () => { dialog.remove(); button.focus(); };
        close.addEventListener('click', () => dialog.close()); dialog.addEventListener('close', cleanup, { once: true });
        dialog.showModal(); close.focus();
      });
    });
  }
  window.OxxVnextFinder = { matches, fromIndex, quoteUrl, engineerUrl, standardCode, groupMatches, nearbySizes };
  const boot = () => { document.querySelectorAll('[data-oxx-finder]').forEach(init); initHelp(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
